import { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  LayoutDashboard,
  Tag,
  ShoppingCart,
  Settings,
  LogOut,
  Home,
  Users,
  Calendar,
  Menu,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { AdminRole } from '@/types/database'
import { BrandLogo } from '@/components/brand-logo'

interface NavItem {
  title: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  roles?: AdminRole[]
}

const adminNavItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/admin',
    icon: LayoutDashboard,
  },
  {
    title: 'Drops',
    href: '/admin/drops',
    icon: Calendar,
  },
  {
    title: 'Products',
    href: '/admin/products',
    icon: Tag,
  },
  {
    title: 'Orders',
    href: '/admin/orders',
    icon: ShoppingCart,
  },
  {
    title: 'Team',
    href: '/admin/team',
    icon: Users,
    roles: ['owner', 'admin'], // Only visible to owner and admin
  },
  {
    title: 'Settings',
    href: '/admin/settings',
    icon: Settings,
    roles: ['owner', 'admin'], // Only visible to owner and admin
  },
]

function getVisibleNavItems(items: NavItem[], adminRole: AdminRole | null): NavItem[] {
  return items.filter((item) => {
    if (!item.roles) return true
    if (!adminRole) return false
    return item.roles.includes(adminRole)
  })
}

function getAdminPageTitle(pathname: string, visibleNavItems: NavItem[]): string {
  const directMatch = visibleNavItems.find((item) => {
    if (item.href === '/admin') return pathname === '/admin'
    return pathname === item.href || pathname.startsWith(`${item.href}/`)
  })

  if (directMatch) return directMatch.title
  if (pathname === '/admin/products/new') return 'New Product'
  if (pathname.startsWith('/admin/products/') && pathname.endsWith('/edit')) return 'Edit Product'
  if (pathname.startsWith('/admin/orders/')) return 'Order Details'
  if (pathname === '/admin/team/invite') return 'Invite Team Member'
  return 'Admin'
}

function AdminNavLinks({
  items,
  pathname,
  onNavigate,
}: {
  items: NavItem[]
  pathname: string
  onNavigate?: () => void
}) {
  return (
    <nav className="space-y-1">
      {items.map((item) => {
        const isActive =
          pathname === item.href ||
          (item.href !== '/admin' && pathname.startsWith(item.href))

        return (
          <Link
            key={item.href}
            to={item.href}
            onClick={onNavigate}
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            <item.icon className="h-4 w-4" />
            {item.title}
          </Link>
        )
      })}
    </nav>
  )
}

function AdminSidebarFooter({
  onLogout,
  onNavigate,
}: {
  onLogout: () => void
  onNavigate?: () => void
}) {
  return (
    <div className="border-t p-4 space-y-2">
      <Button
        variant="outline"
        className="w-full justify-start"
        asChild
      >
        <Link to="/" onClick={onNavigate}>
          <Home className="mr-2 h-4 w-4" />
          View Site
        </Link>
      </Button>
      <Button
        variant="ghost"
        className="w-full justify-start text-muted-foreground"
        onClick={onLogout}
      >
        <LogOut className="mr-2 h-4 w-4" />
        Log Out
      </Button>
    </div>
  )
}

export function AdminSidebar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { adminRole } = useAuth()

  const visibleNavItems = useMemo(
    () => getVisibleNavItems(adminNavItems, adminRole),
    [adminRole]
  )

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/?auth=signin')
  }

  return (
    <aside className="hidden h-screen w-64 shrink-0 flex-col border-r bg-background lg:sticky lg:top-0 lg:flex">
      <div className="flex h-16 items-center border-b px-6">
        <Link to="/admin" className="flex items-center gap-2">
          <BrandLogo size="sm" />
          <span className="text-lg font-bold">Admin</span>
        </Link>
      </div>

      <ScrollArea className="flex-1 px-3 py-4">
        <AdminNavLinks items={visibleNavItems} pathname={location.pathname} />
      </ScrollArea>

      <AdminSidebarFooter onLogout={handleLogout} />
    </aside>
  )
}

export function AdminMobileHeader() {
  const location = useLocation()
  const navigate = useNavigate()
  const { adminRole } = useAuth()
  const [open, setOpen] = useState(false)

  const visibleNavItems = useMemo(
    () => getVisibleNavItems(adminNavItems, adminRole),
    [adminRole]
  )
  const pageTitle = useMemo(
    () => getAdminPageTitle(location.pathname, visibleNavItems),
    [location.pathname, visibleNavItems]
  )

  const handleLogout = async () => {
    await supabase.auth.signOut()
    setOpen(false)
    navigate('/?auth=signin')
  }

  return (
    <div className="sticky top-0 z-30 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 lg:hidden">
      <div className="flex h-14 items-center justify-between px-4">
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground"
          onClick={() => setOpen(true)}
        >
          <Menu className="h-5 w-5" />
          <span className="sr-only">Open admin navigation</span>
        </Button>
        <p className="max-w-[70%] truncate text-sm font-semibold">{pageTitle}</p>
        <div className="w-9" />
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[18rem] max-w-[85vw] p-0">
          <SheetHeader className="border-b">
            <SheetTitle className="flex items-center gap-2">
              <BrandLogo size="sm" />
              <span>Admin</span>
            </SheetTitle>
            <SheetDescription className="sr-only">
              Admin navigation
            </SheetDescription>
          </SheetHeader>

          <div className="flex h-full flex-col">
            <ScrollArea className="flex-1 px-3 py-4">
              <AdminNavLinks
                items={visibleNavItems}
                pathname={location.pathname}
                onNavigate={() => setOpen(false)}
              />
            </ScrollArea>
            <AdminSidebarFooter onLogout={handleLogout} onNavigate={() => setOpen(false)} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
